import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, useTextoDeError } from '../componentes/ui/estados';
import { useSesion } from '../sesion/ProveedorDeSesion';
import {
  DATOS_FISCALES_INICIALES,
  DatosFiscalesDelComercio,
  type PaisDelAlta,
} from '../componentes/DatosFiscalesDelComercio';

interface PerfilDeNegocio {
  readonly codigo: string;
  readonly claveDeTraduccion: string;
}

/**
 * Alta guiada del comercio.
 *
 * Tres pasos y no un formulario largo: quien lo completa está dando de alta su negocio por
 * primera vez y no sabe qué le van a preguntar. Ver veinte campos de una hace abandonar;
 * ver cuatro, con el título de lo que se está definiendo, no.
 *
 * Se puede volver atrás sin perder lo escrito, porque el primer intento de cargar un CUIT
 * suele terminar en "andá a buscar el papel".
 *
 * El país va con los datos fiscales y no con «cómo trabajás»: decide cómo se llama la clave, qué
 * condiciones existen y en qué moneda opera el comercio (E35).
 */
export function AltaDeEmpresa({
  alTerminar,
}: {
  readonly alTerminar?: (() => void) | undefined;
} = {}): React.ReactElement {
  const { t } = useTranslation();
  const { recargarPerfil, pararseEn, salir } = useSesion();
  const textoDeError = useTextoDeError();

  const [paso, setPaso] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [datos, setDatos] = useState({
    nombre: '',
    razonSocial: '',
    ...DATOS_FISCALES_INICIALES,
    perfilDeNegocio: '',
    sucursal: { codigo: 'CENTRO', nombre: '', ciudad: '' },
  });

  const opciones = useQuery({
    queryKey: ['empresas', 'opciones'],
    queryFn: () =>
      pedir<{ paises: PaisDelAlta[]; perfiles: PerfilDeNegocio[] }>('/empresas/opciones'),
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ empresaId: string }>('/empresas', {
        method: 'POST',
        cuerpo: {
          ...datos,
          jurisdiccionCodigo:
            datos.jurisdiccionCodigo === '' ? undefined : datos.jurisdiccionCodigo,
          sucursal: {
            ...datos.sucursal,
            ciudad: datos.sucursal.ciudad === '' ? undefined : datos.sucursal.ciudad,
          },
        },
      }),
    onSuccess: async (respuesta) => {
      await recargarPerfil();
      await pararseEn(respuesta.empresaId);
      alTerminar?.();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const pasos = [t('alta.paso1'), t('alta.paso2'), t('alta.paso3')];

  if (opciones.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Cargando />
      </div>
    );
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-8">
      <div className="w-full max-w-lg rounded-marco border border-borde bg-superficie p-6">
        <h1 className="text-lg font-semibold tracking-tight">{t('alta.titulo')}</h1>
        <p className="mb-4 text-xs text-texto-tenue">
          {t('alta.progreso', { actual: paso + 1, total: pasos.length })}
          {' · '}
          {pasos[paso]}
        </p>

        <div className="flex flex-col gap-3">
          {paso === 0 ? (
            <>
              <Campo
                etiqueta={t('alta.nombre')}
                obligatorio
                value={datos.nombre}
                ayuda={t('alta.ayudaNombre')}
                onChange={(evento) => {
                  setDatos((previo) => ({ ...previo, nombre: evento.target.value }));
                }}
              />
              <Campo
                etiqueta={t('alta.razonSocial')}
                obligatorio
                value={datos.razonSocial}
                ayuda={t('alta.ayudaRazonSocial')}
                onChange={(evento) => {
                  setDatos((previo) => ({ ...previo, razonSocial: evento.target.value }));
                }}
              />
              <DatosFiscalesDelComercio
                paises={opciones.data?.paises ?? []}
                valor={datos}
                alCambiar={(fiscales) => {
                  setDatos((previo) => ({ ...previo, ...fiscales }));
                }}
              />
            </>
          ) : null}

          {paso === 1 ? (
            <>
              <Selector
                etiqueta={t('alta.perfil')}
                obligatorio
                value={datos.perfilDeNegocio}
                ayuda={t('alta.ayudaPerfil')}
                onChange={(evento) => {
                  setDatos((previo) => ({ ...previo, perfilDeNegocio: evento.target.value }));
                }}
              >
                <option value="" disabled>
                  {t('alta.elegirPerfil')}
                </option>
                {(opciones.data?.perfiles ?? []).map((perfil) => (
                  <option key={perfil.codigo} value={perfil.codigo}>
                    {t(perfil.claveDeTraduccion, { defaultValue: perfil.codigo })}
                  </option>
                ))}
              </Selector>
            </>
          ) : null}

          {paso === 2 ? (
            <>
              <Campo
                etiqueta={t('alta.sucursalNombre')}
                obligatorio
                value={datos.sucursal.nombre}
                ayuda={t('alta.ayudaSucursal')}
                onChange={(evento) => {
                  setDatos((previo) => ({
                    ...previo,
                    sucursal: { ...previo.sucursal, nombre: evento.target.value },
                  }));
                }}
              />
              <Campo
                etiqueta={t('alta.sucursalCiudad')}
                value={datos.sucursal.ciudad}
                onChange={(evento) => {
                  setDatos((previo) => ({
                    ...previo,
                    sucursal: { ...previo.sucursal, ciudad: evento.target.value },
                  }));
                }}
              />
              <p className="text-xs text-texto-tenue">{t('alta.loQueSeCrea')}</p>
            </>
          ) : null}

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}

          <div className="mt-2 flex items-center justify-between gap-2">
            <Boton
              tono="sutil"
              onClick={() => {
                if (paso === 0) void salir();
                else setPaso((previo) => previo - 1);
              }}
            >
              {paso === 0 ? t('acciones.salir') : t('acciones.volver')}
            </Boton>

            {paso < pasos.length - 1 ? (
              <Boton
                tono="principal"
                onClick={() => {
                  setPaso((previo) => previo + 1);
                }}
              >
                {t('acciones.siguiente')}
              </Boton>
            ) : (
              <Boton
                tono="principal"
                cargando={crear.isPending}
                onClick={() => {
                  crear.mutate();
                }}
              >
                {t('alta.crear')}
              </Boton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
